import React, { useState } from 'react';
import {
  Heading,
  Button,
  Input,
  Box,
  Flex,
  Text,
  Icon,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { FaCarSide } from 'react-icons/fa';
import { Trans, t } from '@lingui/macro';

interface ReportSearchCardProps {
  onSearch: (query: string, isReportNumber: boolean) => void;
}

export const ReportSearchCard: React.FC<ReportSearchCardProps> = ({ onSearch }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const headingColor = useColorModeValue('gray.800', 'white');
  const accentColor = useColorModeValue('blue.500', 'blue.300');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const buttonBg = useColorModeValue('blue.500', 'blue.600');
  const buttonHoverBg = useColorModeValue('blue.600', 'blue.700');
  const inputBg = useColorModeValue('gray.50', 'gray.700');

  // Check if query is a report number (only digits)
  const isReportNumber = (query: string) => /^\d+$/.test(query.trim());

  const handleSearch = () => {
    if (searchQuery.trim()) {
      onSearch(searchQuery, isReportNumber(searchQuery));
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      onSearch(searchQuery, isReportNumber(searchQuery));
    }
  };

  return (
    <Box height="100%" width="100%" display="flex" flexDirection="column" minHeight="300px">
      <Flex mb={4} align="center">
        <Icon as={FaCarSide} color={accentColor} mr={2} boxSize={5} />
        <Heading size="md" color={headingColor}>
          <Trans>Rapor Sorgulama</Trans>
        </Heading>
      </Flex>

      <Box
        flex="1"
        bg={bgColor}
        borderWidth="1px"
        borderRadius="lg"
        borderColor={borderColor}
        position="relative"
        overflow="hidden"
        boxShadow="sm"
        p={5}
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '4px',
          height: '100%',
          bg: accentColor,
        }}
      >
        <Text mb={5} color={textColor}>
          <Trans>
            Araç plakası veya rapor numarası girerek hızlı rapor sorgulaması yapabilirsiniz. Sonuçlar otomatik olarak
            listeye eklenecektir.
          </Trans>
        </Text>

        <InputGroup size="lg" mb={6}>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color={accentColor} />
          </InputLeftElement>
          <Input
            placeholder={t`Plaka veya Rapor No`}
            bg={inputBg}
            borderRadius="md"
            borderColor={borderColor}
            _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyPress}
            size="lg"
          />
        </InputGroup>

        <Button
          leftIcon={<SearchIcon />}
          w="100%"
          size="lg"
          color="white"
          bg={buttonBg}
          _hover={{ bg: buttonHoverBg }}
          borderRadius="md"
          fontWeight="semibold"
          boxShadow="sm"
          onClick={handleSearch}
          isDisabled={!searchQuery.trim()}
        >
          <Trans>Rapor Sorgula</Trans>
        </Button>
      </Box>
    </Box>
  );
};
